<script lang="ts">
  // BJL-CHECK: Button, RadioGroup — considered, neither fits. The figures are a
  // hover-cumulative rating scale (hovering N fills 1..N) rendering an SVG silhouette, and
  // submit fires on click with no selected-value state to bind; bjl RadioGroup renders
  // labelled inputs and Button has no cumulative-hover mode.
  // See artifacts/deviations/CrowdingReport.md.
  //
  // Compact 5-figure rating control: hovering the Nth figure highlights 1..N, clicking it
  // submits crowding level N.
  //
  // ONE paint, the app's own — `Colors` and the `Type` scale, with the figures filled in the
  // vehicle's MTA route color. There used to be a second, `led`, which set the control in the
  // rollsign's register: VT323, phosphor glow, amber lamps, and "CROWD?" shortened to fit. It
  // looked like the sign it sat under, and that was the problem — this is app chrome that
  // happens to sit on the riding panel, not part of the depiction of a car front. The sign is
  // `Rider/DestinationSign.svelte` and the strip; everything below them is the app talking.
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { CROWDING_LEVELS, type CrowdingState } from './crowding.svelte'

  interface Props {
    crowding: CrowdingState
    accentColor: string
    /**
     * Which way it lays out, and nothing about how it is painted — the two used to be one
     * `variant` prop, which is what made "put it in a row" mean "set it in the rollsign's
     * face". `row` is what both riding-panel faces now pass: each puts this in a control row
     * beside the 3D toggle and ✕, where height is scarce and the label is the only thing that
     * can give way. `stack` remains the default for a caller with width to spare and height to
     * fill; the bus footer was that caller until its own controls moved into it.
     */
    layout?: 'stack' | 'row'
  }

  const { crowding, accentColor, layout = 'stack' }: Props = $props()

  let hoverLevel = $state<number | null>(null)

  const disabled = $derived(crowding.submitting || crowding.onCooldown)
  // While hovering, fill up to the hovered figure; otherwise reflect the user's own
  // just-reported level. Figure i is filled when its level <= the active level.
  const activeLevel = $derived(hoverLevel ?? crowding.justReportedLevel ?? 0)

  const row = $derived(layout === 'row')
</script>

<div
  style="
    display: flex; gap: {row ? '6px' : '3px'};
    {row ? 'align-items: center;' : 'flex-direction: column; align-items: center;'}
  "
>
  <!-- The riding panel's control row is TIGHT — ~343px of a 360px phone's 352 — and this is
       the only part of it that flexes, so it is the label that gives way on anything narrower
       rather than a control being pushed off the end. -->
  <span
    style="
      line-height: 1; white-space: nowrap; min-width: 0;
      overflow: hidden; text-overflow: ellipsis;
      color: {Colors.textMuted}; {Type.caption};
    "
  >
    {crowding.onCooldown ? 'Thanks!' : 'Crowded?'}
  </span>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    style="display: flex; flex-shrink: 0; gap: 2px; align-items: flex-end;"
    onmouseleave={() => { hoverLevel = null }}
  >
    {#each CROWDING_LEVELS as { level, label } (level)}
      {@const filled = level <= activeLevel}
      <button
        onclick={() => crowding.onReport(level)}
        onmouseenter={() => { if (!disabled) hoverLevel = level }}
        {disabled}
        title={label}
        aria-label="{label} ({level} of 5)"
        style="
          display: flex; justify-content: center; align-items: center;
          padding: 5px 4px;
          border: none; background: none;
          cursor: {disabled ? 'default' : 'pointer'};
          opacity: {disabled && !filled ? 0.5 : 1};
          transition: transform 0.08s ease;
          transform: {hoverLevel === level ? 'scale(1.18)' : 'none'};
        "
      >
        <!-- Generous padding above, not a bigger glyph: keeps the row visually compact
             while giving each level a finger-sized (~28 px) target so taps don't land one off. -->
        <svg width="18" height={18 * 1.22} viewBox="0 0 18 22" aria-hidden="true" style="display: block;">
          <path
            d="M9 0.5a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8ZM3 21.5v-7.2C3 11 5.7 9 9 9s6 2 6 5.3v7.2H3Z"
            fill={filled ? accentColor : 'none'}
            stroke={filled ? accentColor : Colors.borderStrong}
            stroke-width="1.6"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    {/each}
  </div>
  {#if crowding.error}
    <span style="color: {Colors.warning}; {Type.caption};">{crowding.error}</span>
  {/if}
</div>
